<template>
  <auth-form-wrapper
    :title="$t('auth.reset-password.title')"
    :subtitle="$t('auth.reset-password.subtitle')"
    :error="error"
  >
    <b-form class="auth-form mt-2" @submit.prevent="handleSubmit">
      <!-- Password -->
      <div class="mb-1">
        <label class="form-label">
          {{ $t('auth.reset-password.form.password.label') }}
        </label>
        <div class="input-group input-group-merge">
          <b-form-input
            id="reset-password"
            v-model="formData.password"
            :type="passwordFieldType"
            name="password"
            :placeholder="$t('auth.reset-password.form.password.placeholder')"
            class="form-control-merge"
            required
          />
          <div class="input-group-append">
            <div class="input-group-text cursor-pointer" @click="togglePasswordVisibility">
              <feather-icon
                :icon="passwordToggleIcon"
                size="16"
                class="toggle-password-icon"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Repeat Password -->
      <div class="mb-1">
        <label class="form-label">
          {{ $t('auth.reset-password.form.password-confirm.label') }}
        </label>
        <div class="input-group input-group-merge">
          <b-form-input
            id="reset-password-confirm"
            v-model="formData.repeatPassword"
            :type="passwordFieldType"
            name="repeatPassword"
            :placeholder="$t('auth.reset-password.form.password-confirm.placeholder')"
            class="form-control-merge"
            required
          />
          <div class="input-group-append">
            <div class="input-group-text cursor-pointer" @click="togglePasswordVisibility">
              <feather-icon
                :icon="passwordToggleIcon"
                size="16"
                class="toggle-password-icon"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="auth-btn-container d-flex justify-content-center">
        <b-button
          type="submit"
          variant="primary"
          class="auth-btn auth-btn-primary mt-2 mb-2"
          :disabled="invalid"
        >
          {{ $t('auth.reset-password.form.action.submit') }}
        </b-button>
      </div>
    </b-form>

    <!-- Cancel Link -->
    <p class="text-center mt-2">
      <b-link class="auth-link" :to="{ name: 'auth-login' }">
        {{ $t('common.cancel') }}
      </b-link>
    </p>
  </auth-form-wrapper>
</template>

<script>
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import AuthFormWrapper from '@/auth/components/AuthFormWrapper.vue'

export default {
  name: 'ResetPassword',

  components: {
    AuthFormWrapper
  },

  inject: {
    authApiUrl: {
      default: process.env.VUE_APP_AUTH_API_URL || 'https://auth-api.nectios.com'
    },
    isNexos: {
      default: () => false
    }
  },

  mixins: [togglePasswordVisibility],

  data() {
    return {
      formData: {
        password: '',
        repeatPassword: ''
      },
      error: null,
      invalid: false
    }
  },

  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    communitySlug() {
      return this.$route.params.communityId
    },
    token() {
      return this.$route.query.token
    },
    // AuthRoot provides isNexos as a function; calling it is required (a bare function is always truthy).
    isNexosTenant() {
      return typeof this.isNexos === 'function' ? this.isNexos() : !!this.isNexos;
    }
  },

  watch: {
    'formData.password'(newVal) {
      this.validateForm()
    },
    'formData.repeatPassword'(newVal) {
      this.validateForm()
    }
  },

  methods: {
    validateForm() {
      // Check password requirements
      const hasUpperCase = /[A-Z]/.test(this.formData.password);
      const hasLowerCase = /[a-z]/.test(this.formData.password);
      const hasNumber = /[0-9]/.test(this.formData.password);
      const isLongEnough = this.formData.password.length >= 6;

      // Check if all password requirements are met
      const passwordValid = hasUpperCase && hasLowerCase && hasNumber && isLongEnough;

      // Show appropriate error messages
      if (this.formData.password.length > 0) {
        if (!isLongEnough) {
          this.error = this.isNexosTenant ? 'La contraseña no puede ser menos de 6 caracteres' : 'Password cannot be less than 6 characters in length';
        } else if (!hasUpperCase) {
          this.error = this.isNexosTenant ? 'La contraseña debe contener al menos una letra mayúscula' : 'Password must contain at least one uppercase letter';
        } else if (!hasLowerCase) {
          this.error = this.isNexosTenant ? 'La contraseña debe contener al menos una letra minúscula' : 'Password must contain at least one lowercase letter';
        } else if (!hasNumber) {
          this.error = this.isNexosTenant ? 'La contraseña debe contener al menos un número' : 'Password must contain at least one number';
        } else {
          this.error = null;
        }
      }

      this.invalid = !this.formData.password || 
                    !this.formData.repeatPassword ||
                    !passwordValid ||
                    this.formData.password !== this.formData.repeatPassword;
    },

    async handleSubmit() {
      if (this.formData.password !== this.formData.repeatPassword) {
        this.error = 'auth.reset-password.error.password-mismatch'
        return
      }

      const endpoint = this.authApiUrl + 'password/reset-with-token';
      try {
        const response = await fetch(
          endpoint,
          {
            method: 'POST',
            body: JSON.stringify({
              ...this.formData,
              token: this.token,
              locale: this.$store.state.locale.currentLocale,
              communitySlug: this.communitySlug
            }),
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          throw new Error()
        }

        // Redirect to login
        this.$router.push({
          name: 'auth-login',
          params: { 
            communityId: this.communitySlug
          }
        })
      } catch (error) {
        console.error('Error:', error)
        this.error = 'auth.reset-password.error.general'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.input-group-merge {
  display: flex;
  position: relative;
  flex-wrap: wrap;
  align-items: stretch;
  width: 100%;

  .form-control-merge {
    position: relative;
    flex: 1 1 auto;
    width: 1%;
    min-width: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .input-group-append {
    display: flex;
    margin-left: -1px;

    .input-group-text {
      display: flex;
      align-items: center;
      padding: 0.571rem 1rem;
      margin-bottom: 0;
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.45;
      color: #6e6b7b;
      text-align: center;
      white-space: nowrap;
      background-color: #f8f8f8;
      border: 1px solid #d8d6de;
      border-left: 0;
      border-top-right-radius: 0.357rem;
      border-bottom-right-radius: 0.357rem;
      
      &:hover {
        background-color: #efefef;
      }
    }
  }

  .toggle-password-icon {
    cursor: pointer;
  }
}

.cursor-pointer {
  cursor: pointer;
}
</style>
